import { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import FullScreenVideoPostCard from "./FullScreenVideoPostCard";
// import Spinner from "../../components/Spinner";
import { getAllVideos } from "../../redux/actions/postActions";
import SkeletonCompactPost from "../SkeletonCompactPost/SkeletonCompactPost";
import {
  IonContent,
  IonPage,
  useIonRouter,
  useIonViewDidEnter,
  useIonViewDidLeave,
  useIonViewWillEnter,
} from "@ionic/react";
import { RiHome4Line } from "react-icons/ri";
import { Helmet } from "react-helmet-async";
import "./videos.css";

function Videos() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [visibleVideoId, setVisibleVideoId] = useState(null);
  const location = useLocation();
  const router = useIonRouter();
  const observerRef = useRef(null);

  const postState = useSelector((state) => state.post);
  const { allVideos, modifiedPost } = postState;

  const dispatch = useDispatch();

  // Status bar background for PWA standalone (works on iOS and Android)
  useIonViewDidEnter(() => {
    document.body.classList.add("videos-page");
    document.documentElement.classList.add("videos-page");

    // Force theme-color to black while on this page (affects Safari UI and PWA status bar)
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) metaTheme.setAttribute("content", "#000000");
  });

  useIonViewDidLeave(() => {
    // const metaTheme = document.querySelector('meta[name="theme-color"]');
    // const prevTheme = metaTheme ? metaTheme.getAttribute("content") : null;
    document.body.classList.remove("videos-page");
    document.documentElement.classList.remove("videos-page");
    // if (metaTheme && prevTheme) metaTheme.setAttribute("content", prevTheme);
  });

  useIonViewWillEnter(() => {
    const params = new URLSearchParams(location.search);
    dispatch(
      getAllVideos({ id: params.get("id"), page, oneVideoPerUser: true }),
    );
  }, [dispatch, page, modifiedPost, location.search]);

  // Function to pause all videos except the visible one
  const pauseAllVideosExcept = useCallback((visibleId) => {
    const allVideos = document.querySelectorAll("video");
    allVideos.forEach((video) => {
      const videoContainer = video.closest(".video-post-container");
      if (videoContainer) {
        const postId = videoContainer.getAttribute("data-post-id");
        if (postId !== visibleId) {
          video.pause();
          video.muted = true;
        } else {
          video.muted = false;
          video.currentTime = 0; // Start from the beginning
          if (video.paused) {
            video.play().catch((e) => console.warn("Video play failed:", e));
          }
        }
      }
    });
  }, []);

  // Set up intersection observer
  useEffect(() => {
    if (!allVideos || allVideos.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const postId = entry.target.getAttribute("data-post-id");
            setVisibleVideoId(postId);
            pauseAllVideosExcept(postId);
          } else {
            // 2. SCROLLED OUT: Mute and Pause (The Facebook behavior)
            const video = entry.target.querySelector("video");
            if (video) {
              video.muted = true;
              video.pause(); // Stop playback to save battery
            }
          }
        });
      },
      {
        threshold: 0.5, // Video must be at least 50% visible
        rootMargin: "0px",
      },
    );

    observerRef.current = observer;

    // Observe all video containers
    const videoContainers = document.querySelectorAll(".video-post-container");
    videoContainers.forEach((container) => {
      observer.observe(container);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [allVideos, pauseAllVideosExcept]);

  const handleHomeClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Then navigate to home
    router.push("/", "forward");
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <IonPage className="videos-ion-page">
        <IonContent
          fullscreen={true}
          className="no-padding videos-ion-content"
          scrollY={false}
        >
          <div className="explore-wrapper">
            <div className="videos">
              {allVideos ? (
                allVideos.length > 0 ? (
                  <InfiniteScroll
                    dataLength={allVideos.length}
                    next={() => setPage(page + 1)}
                    scrollableTarget="video-scroll-container"
                    hasMore={true}
                  >
                    <div id="video-scroll-container">
                      {allVideos.map((post, i) => (
                        <FullScreenVideoPostCard
                          post={post}
                          key={post._id + i}
                          isVisible={visibleVideoId === post._id}
                          postId={post._id}
                        />
                      ))}
                    </div>
                  </InfiniteScroll>
                ) : (
                  <div className="display-flex flex-direction-c align-items-c">
                    <div className="view-wrapper ">
                      <button
                        onClick={handleHomeClick}
                        title="Go Home"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <RiHome4Line className="post-btns__btn post-btns__btn_icon" />
                      </button>
                      <h1>{t("noPostsTitle")}</h1>
                      <p>{t("explore.noPosts")}</p>
                    </div>
                  </div>
                )
              ) : (
                <>
                  <SkeletonCompactPost />
                  <SkeletonCompactPost />
                  <SkeletonCompactPost />
                </>
              )}
            </div>
          </div>
        </IonContent>
      </IonPage>
    </>
  );
}

export default Videos;

